import { isValidUuid } from '@/util/is-valid-uuid'
import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { ViewerDocumentKind, ViewerDownloadTargetKind, ViewerOpenContextKind } from '@/content-viewer/store/schemas'
import { useAppDispatch } from '@/store/store-hooks'

import { useDraftWorkspace } from '@/drafting/workspace/hooks/useDraftWorkspace'
import { useSourceOpensInChatPanel } from '@/drafting/workspace/hooks/useSourceOpensInChatPanel'
import { openStandaloneViewerTab } from '@/standalone-reference-viewer/utils/open-standalone-viewer-tab'

/** Open table source-file links through the same viewer state used by chat citations. */
export function useOpenAgentV2FileLink(conversationId: string) {
  const { matterId } = useParams()
  const dispatch = useAppDispatch()
  const { draftArtifactVersionId } = useDraftWorkspace()
  const sourceOpensInChatPanel = useSourceOpensInChatPanel()

  return useCallback(
    (href: string, title: string): boolean => {
      let url: URL
      try {
        url = new URL(href, window.location.origin)
      } catch {
        return false
      }
      if (url.origin !== window.location.origin) return false
      const match = url.pathname.match(/^\/dashboard\/m\/([^/]+)\/reference-viewer\/user-file\/([^/]+)$/)
      if (!match || match[1] !== matterId || !isValidUuid(match[2])) return false
      if (draftArtifactVersionId != null && !sourceOpensInChatPanel) return openStandaloneViewerTab(url.href)
      const userFileId = match[2]
      const page = url.searchParams.get('page')
      const startingPage = page && /^\d+$/.test(page) ? Number.parseInt(page, 10) : undefined
      const excerpt = new URLSearchParams(url.hash.slice(1)).get('excerpt')
      dispatch(
        ContentViewerActions.setContentViewerSession({
          session: {
            document: {
              kind: ViewerDocumentKind.USER_FILE,
              userFileId,
              title,
              downloadTarget: { kind: ViewerDownloadTargetKind.USER_FILE_ORIGINAL, userFileId },
            },
            context: { kind: ViewerOpenContextKind.SELECTED_FILE, conversationId },
            presentation: {
              ...(startingPage !== undefined ? { startingPage } : {}),
              ...(excerpt ? { pdfExcerpt: excerpt, markdownHighlights: [excerpt] } : {}),
            },
          },
        })
      )
      return true
    },
    [conversationId, dispatch, matterId, draftArtifactVersionId, sourceOpensInChatPanel]
  )
}
